import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from '../../components/ui';
import { Modal } from '../../components/ui';
import { sideChipClass, inviteChipClass, visibilityChipClass, fullName, INVITE_STATUSES } from '../../utils/helpers';
import MemberForm from './MemberForm';
import ContactImport from './ContactImport';
import FamilyMemberForm from './FamilyMemberForm';

export default function MembersMobile({ vm }) {
  const {
    isAdmin, canAdd, canEdit, canDelete, mine, functions, rows, loading, filtered, stats,
    q, setQ, fnFilter, setFnFilter, sideFilter, setSideFilter, fnName, adderCount, openAdd, openEdit, mark, del, open,
    canBulkImport, openBulk, bulkOpen, inviteRow, inviteSharing, inviteCooldown, openInvite, closeInvite, sendInvite, guestHasAllFunctions,
    addOrMoveFamilyMember, isMarriage, sideOptions, sort, setSort, SORT_OPTIONS,
  } = vm;
  const [expandedFamilyId, setExpandedFamilyId] = useState(null);
  const [expandedGuestId, setExpandedGuestId] = useState(null);
  const [actionCooldown, setActionCooldown] = useState(0);
  const [familyFormHead, setFamilyFormHead] = useState(null);
  const [guestTypeFilter, setGuestTypeFilter] = useState('all');

  useEffect(() => {
    if (actionCooldown <= 0) return undefined;
    const timer = setTimeout(() => setActionCooldown(seconds => Math.max(0, seconds - 1)), 1000);
    return () => clearTimeout(timer);
  }, [actionCooldown]);

  const mobileCards = useMemo(() => {
    const cards = [];
    const families = new Map();
    const allFamilyMembers = new Map();
    rows.forEach(row => {
      const familyId = Number(row.FamilyId || 0);
      if (!familyId) return;
      if (!allFamilyMembers.has(familyId)) allFamilyMembers.set(familyId, []);
      if (!allFamilyMembers.get(familyId).some(member => String(member.GuestId) === String(row.GuestId))) {
        allFamilyMembers.get(familyId).push(row);
      }
    });
    filtered.forEach(row => {
      const familyId = Number(row.FamilyId || 0);
      if (!familyId) {
        cards.push({ type: 'guest', key: `guest-${row.GuestId}`, row });
        return;
      }
      if (!families.has(familyId)) {
        const family = { type: 'family', key: `family-${familyId}`, familyId, members: [] };
        families.set(familyId, family);
        cards.push(family);
      }
      families.get(familyId).members = allFamilyMembers.get(familyId) || [row];
    });
    families.forEach(family => {
      family.members.sort((a, b) => {
        if (String(a.GuestId) === String(family.familyId)) return -1;
        if (String(b.GuestId) === String(family.familyId)) return 1;
        return Number(a.GuestId) - Number(b.GuestId);
      });
      family.head = family.members.find(row => String(row.GuestId) === String(family.familyId))
        || family.members[0];
    });
    return cards;
  }, [filtered, rows]);
  // Cards keep the order of vm.filtered, which is already sorted by vm.sort
  // (same Sort dropdown as the desktop table).
  const visibleCards = useMemo(() => mobileCards.filter(card =>
    guestTypeFilter === 'all'
      || (guestTypeFilter === 'family' && card.type === 'family')
      || (guestTypeFilter === 'individual' && card.type === 'guest')
  ), [mobileCards, guestTypeFilter]);

  const cooldown = actionCooldown;
  const beginFamilyAction = (action) => {
    if (cooldown > 0) return;
    setActionCooldown(5);
    action();
  };
  const callGuest = (row) => {
    const mobile = String(row.MobileNumber || '').replace(/\D/g, '');
    if (!mobile) return;
    if (window.AndroidDialer?.openDialer) window.AndroidDialer.openDialer(mobile);
    else window.location.href = `tel:${mobile}`;
  };

  const GuestDetails = ({ row, familyMember = false }) => {
    const editable = mine(row);
    const count = adderCount(row);
    return (
      <>
        <div className="mobile-card-head">
          <div style={{ minWidth: 0 }}>
            <strong className="mobile-title">{fullName(row)}</strong>
            <div className="muted" style={{ fontSize: 12 }}>
              Added by {(row.AddedByName || row.InvitedByName || '').trim() || '-'}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <span className={sideChipClass(row.Side)}>{row.Side}</span>
        </div>

        <div className="mobile-meta">
          <div>Mobile: {row.MobileNumber || '-'}</div>
          <div>
            {row.FunctionNames || row.FunctionName || fnName(row.FunctionId)}
            <span className={visibilityChipClass(row.Visibility)} style={{ marginLeft: 6 }}>{row.Visibility || 'Private'}</span>
          </div>
          {isAdmin && (
            <div className="muted">
              Invited by {(row.AddedByName || row.InvitedByName || '').trim() || '-'}
              {count > 1 && <span className="chip warn" style={{ marginLeft: 6 }}>{count} members</span>}
            </div>
          )}
        </div>

        <div className="mobile-actions family-member-actions" style={{ alignItems: 'center' }}>
          {editable && canEdit ? (
            <select value={row.InviteStatus || 'Pending'} disabled={familyMember && cooldown > 0}
              onChange={e => familyMember
                ? beginFamilyAction(() => mark(row, e.target.value))
                : mark(row, e.target.value)}
              className={inviteChipClass(row.InviteStatus)}
              style={{ border: 'none', borderRadius: 999, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>
              {INVITE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <span className={inviteChipClass(row.InviteStatus)}>{row.InviteStatus}</span>
          )}
          {familyMember && row.MobileNumber && (
            <button className="btn light sm" disabled={cooldown > 0}
              onClick={() => beginFamilyAction(() => callGuest(row))}>Call</button>
          )}
          {editable && canEdit && (
            <button className="btn light sm" disabled={familyMember && cooldown > 0}
              onClick={() => familyMember
                ? beginFamilyAction(() => openEdit(row))
                : openEdit(row)}>Edit</button>
          )}
          {row.MobileNumber && (
            <button className="btn primary sm" onClick={() => openInvite(row)}>
              Invite on WhatsApp
            </button>
          )}
          {editable && canDelete && (
            <button className="btn danger sm" disabled={familyMember && cooldown > 0}
              onClick={() => familyMember
                ? beginFamilyAction(() => del(row))
                : del(row)}>Del</button>
          )}
          {!familyMember && canAdd && editable && Number(row.FamilyId || 0) === 0 && (
            <button className="btn light sm" disabled={cooldown > 0}
              onClick={() => beginFamilyAction(() => setFamilyFormHead(row))}>Create Family</button>
          )}
        </div>
      </>
    );
  };

  const CompactSummary = ({ row, family = false, memberCount, expanded, onClick }) => (
    <button type="button" className="guest-card-summary" onClick={onClick}>
      <div className="guest-card-main">
        <strong className="guest-card-name">
          {fullName(row)} {family && <span className="family-label">(Family)</span>}
        </strong>
        <div className="guest-card-sub">
          {row.MobileNumber || 'No phone'}
          {family ? ` · ${memberCount} members` : ''}
        </div>
        <div className="guest-card-functions">
          {String(row.FunctionNames || row.FunctionName || fnName(row.FunctionId))
            .split(',').map(value => value.trim()).filter(Boolean).map((functionName, index) => (
              <span key={`${functionName}-${index}`} className={`function-chip function-chip-${index % 5}`}>
                {functionName}
              </span>
            ))}
        </div>
      </div>
      <span className={sideChipClass(row.Side)}>
        {row.Side === 'Bride' || row.Side === 'Female' ? 'B'
          : row.Side === 'Groom' || row.Side === 'Male' ? 'G' : 'C'}
      </span>
      <span className="family-chevron">{expanded ? '▲' : '▼'}</span>
    </button>
  );

  return (
    <div className="guests-page">
      <div className="mobile-toolbar guest-toolbar-compact guest-controls-sticky">
        <div className="guest-filter-row">
          <select value={fnFilter} onChange={e => setFnFilter(e.target.value)} aria-label="Function filter">
          <option value="">All functions</option>
          {functions.map(f => <option key={f.FunctionId} value={f.FunctionId}>{f.FunctionName}</option>)}
          </select>
          {isMarriage && (
            <select value={sideFilter} onChange={e => setSideFilter(e.target.value)} aria-label="Side filter">
            <option value="">All sides</option>
            <option value="Bride">Bride side</option>
            <option value="Groom">Groom side</option>
            </select>
          )}
          <select value={guestTypeFilter} onChange={e => setGuestTypeFilter(e.target.value)} aria-label="Guest type filter">
            <option value="all">All types</option>
            <option value="family">Families</option>
            <option value="individual">Individuals</option>
          </select>
          <div style={{ display: 'flex', gap: 4, minWidth: 0 }}>
            <select value={sort.key} onChange={e => setSort(x => ({ key: e.target.value, dir: x.dir }))} aria-label="Sort guests" style={{ flex: 1 }}>
              {SORT_OPTIONS.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
            </select>
            <button type="button" className="btn light sm" aria-label="Sort direction" style={{ height: 34, padding: '0 8px', fontSize: 11 }}
              onClick={() => setSort(x => ({ ...x, dir: x.dir === 'asc' ? 'desc' : 'asc' }))}>{sort.dir === 'asc' ? 'A→Z' : 'Z→A'}</button>
          </div>
        </div>
        <div className="guest-search-actions">
          <input placeholder="Search guests..." value={q} onChange={e => setQ(e.target.value)} />
          <div className="guest-add-actions">
            {canAdd && <button className="btn primary" onClick={() => openAdd('individual')} disabled={!functions.length}>+ Guest</button>}
            {canAdd && <button className="btn light" onClick={() => openAdd('family')} disabled={!functions.length}>+ Family</button>}
            {canAdd && canBulkImport && <button className="btn light" onClick={openBulk} disabled={!functions.length}>Contacts</button>}
          </div>
        </div>
      </div>

      {!functions.length && (
        <div className="muted" style={{ marginBottom: 10, fontSize: 13 }}>
          No functions yet. An Event Admin must add functions before guests can be invited.
        </div>
      )}

      <div className="card guest-stats-compact">
        <strong>{visibleCards.length}</strong> shown | <strong>{stats.persons}</strong> persons
        {isMarriage ? <> | <strong>{stats.bride}</strong> bride | <strong>{stats.groom}</strong> groom</> : ' | Guests'}
        {stats.entries !== stats.parties ? ` | ${stats.entries} function invites` : ''}
        {fnFilter ? ` | ${fnName(fnFilter)}` : ''}
      </div>

      {loading ? <div className="card card-pad"><Spinner /></div>
        : visibleCards.length === 0 ? (
          rows.length === 0 ? (
            <div className="card card-pad empty" style={{ padding: '28px 16px' }}>
              <div style={{ fontSize: 34 }}>📇</div>
              <div style={{ fontWeight: 700, color: 'var(--ink)', marginTop: 6 }}>No guests in your list yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {canBulkImport
                  ? 'Pick family and friends straight from your phone contacts — tick many at once.'
                  : 'Add guests one by one, or open the mobile app to pick them from your phone contacts.'}
              </div>
              {canAdd && canBulkImport && (
                <button className="btn primary" onClick={openBulk} disabled={!functions.length} style={{ marginTop: 14, padding: '12px 20px' }}>
                  📇 Add from Contacts
                </button>
              )}
            </div>
          ) : <div className="card card-pad empty">No matching guests.</div>
        ) : (
          <div className="mobile-card-list">
            {visibleCards.map(card => {
              if (card.type === 'guest') {
                const expanded = String(expandedGuestId) === String(card.row.GuestId);
                return (
                  <div key={card.key} className={`card mobile-record-card compact-guest-card ${expanded ? 'expanded' : ''}`}>
                    <CompactSummary
                      row={card.row}
                      expanded={expanded}
                      onClick={() => setExpandedGuestId(expanded ? null : card.row.GuestId)}
                    />
                    {expanded && (
                      <div className="guest-card-expanded">
                        <GuestDetails row={card.row} />
                      </div>
                    )}
                  </div>
                );
              }
              const expanded = String(expandedFamilyId) === String(card.familyId);
              const head = card.head;
              return (
                <div key={card.key} className={`card mobile-record-card family-card ${expanded ? 'expanded' : ''}`}>
                  <CompactSummary
                    row={head}
                    family
                    memberCount={card.members.length}
                    expanded={expanded}
                    onClick={() => setExpandedFamilyId(expanded ? null : card.familyId)}
                  />
                  {expanded && (
                    <div className="family-members">
                      {canAdd && mine(head) && (
                        <button type="button" className="btn primary family-add-member" disabled={cooldown > 0}
                          onClick={() => beginFamilyAction(() => setFamilyFormHead(head))}>
                          {cooldown > 0 ? <><span className="cooldown-ring">{cooldown}</span> Wait</> : '+ Add Member'}
                        </button>
                      )}
                      {card.members.map((member, index) => (
                        <div key={member.GuestId} className="family-member">
                          <div className="family-member-number">{index + 1}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <GuestDetails row={member} familyMember />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      {open && <MemberForm vm={vm} />}
      {familyFormHead && (
        <FamilyMemberForm
          head={familyFormHead}
          rows={rows}
          isMarriage={isMarriage}
          sideOptions={sideOptions}
          onClose={() => setFamilyFormHead(null)}
          onSave={async member => {
            await addOrMoveFamilyMember(familyFormHead, member);
            setExpandedFamilyId(familyFormHead.GuestId);
          }}
        />
      )}
      {inviteRow && (
        <Modal title="Invite on WhatsApp" onClose={closeInvite} footer={<button className="btn light" onClick={closeInvite} disabled={!!inviteSharing}>Cancel</button>}>
          <div style={{ display: 'grid', gap: 10 }}>
            {inviteCooldown > 0 && (
              <div className="invite-cooldown-note">
                Please wait {inviteCooldown} seconds before sending another invitation.
              </div>
            )}
            <button className="btn primary" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('image')}>
              {inviteSharing === 'image' ? 'Opening...' : 'Image Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('text')}>
              {inviteSharing === 'text' ? 'Opening...' : 'Text Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0 || !guestHasAllFunctions(inviteRow)} onClick={() => sendInvite('pdf')}>
              {inviteSharing === 'pdf' ? 'Opening...' : 'PDF Invite'}
            </button>
            {!guestHasAllFunctions(inviteRow) && (
              <div className="muted" style={{ fontSize: 12 }}>PDF invite is available only when this guest is invited to all functions.</div>
            )}
          </div>
        </Modal>
      )}
      {bulkOpen && <ContactImport vm={vm} />}
    </div>
  );
}
